import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Select, Spin } from 'antd'
import {
  FilterOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  HomeOutlined,
  RightOutlined,
} from '@ant-design/icons'
import { useThemeStore } from '@/stores/themeStore'
import { getAllVocData } from '@/services/vocDataService'
import type { VocRecord } from '@/types/vocData'
import AITreemap from './components/AITreemap'
import { buildJTBDTree } from './data'
import type { JTBDNode } from './data'
import './AIExplore.css'

const legendItems = [
  { label: '1-2', color: '#d72426', textColor: '#fff', min: 1, max: 2 },
  { label: '2-3', color: '#e88a3a', textColor: '#fff', min: 2, max: 3 },
  { label: '3-4', color: '#e8c63a', textColor: '#333', min: 3, max: 4 },
  { label: '4-4.5', color: '#6ab04c', textColor: '#fff', min: 4, max: 4.5 },
  { label: '4.5-5', color: '#27864a', textColor: '#fff', min: 4.5, max: 5 },
]

function filterTreeByRange(node: JTBDNode, min: number, max: number): JTBDNode | null {
  if (!node.children || node.children.length === 0) {
    const score = node.satisfactionScore ?? 3
    const inRange = min === 1 ? (score >= min && score <= max) : (score > min && score <= max)
    return inRange ? node : null
  }
  const filteredChildren = node.children
    .map(child => filterTreeByRange(child, min, max))
    .filter(Boolean) as JTBDNode[]
  if (filteredChildren.length === 0) return null
  return { ...node, children: filteredChildren }
}

const ALL_PN = '__ALL__'

const AIExplore = () => {
  const theme = useThemeStore((state) => state.theme)
  const shellRef = useRef<HTMLDivElement>(null)

  const [allData, setAllData] = useState<VocRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedPN, setSelectedPN] = useState<string>(ALL_PN)
  const [selectedJourneys, setSelectedJourneys] = useState<string[]>([])
  const [activeLegend, setActiveLegend] = useState<string | null>(null)
  const [selectedPath, setSelectedPath] = useState<string[]>([])
  const [isFullscreen, setIsFullscreen] = useState(false)

  useEffect(() => {
    getAllVocData()
      .then((data) => setAllData(data))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    const handleChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
    }
    document.addEventListener('fullscreenchange', handleChange)
    return () => document.removeEventListener('fullscreenchange', handleChange)
  }, [])

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      shellRef.current?.requestFullscreen()
    } else {
      document.exitFullscreen()
    }
  }, [])

  const pnOptions = useMemo(() => {
    const pnSet = new Set<string>()
    for (const record of allData) {
      if (record.pn) pnSet.add(record.pn)
    }
    const options = [{ label: '全部产品', value: ALL_PN }]
    for (const pn of Array.from(pnSet).sort()) {
      options.push({ label: pn, value: pn })
    }
    return options
  }, [allData])

  const journeyOptions = useMemo(() => {
    const journeySet = new Set<string>()
    for (const record of allData) {
      if (record.journey) journeySet.add(record.journey)
    }
    const options: { label: string; value: string }[] = []
    for (const j of Array.from(journeySet).sort()) {
      options.push({ label: j, value: j })
    }
    return options
  }, [allData])

  const treeData = useMemo<JTBDNode | null>(() => {
    if (allData.length === 0) return null
    let filtered = selectedPN === ALL_PN
      ? allData
      : allData.filter((r) => r.pn === selectedPN)
    if (selectedJourneys.length > 0) {
      filtered = filtered.filter((r) => selectedJourneys.includes(r.journey))
    }
    if (filtered.length === 0) return null
    const rootName = selectedPN === ALL_PN ? '全部产品' : selectedPN
    return buildJTBDTree(filtered, rootName)
  }, [allData, selectedPN, selectedJourneys])

  const currentData = useMemo(() => {
    if (!treeData) return null
    let current: JTBDNode = treeData
    for (const name of selectedPath) {
      const found = current.children?.find((node) => node.name === name)
      if (!found) break
      current = found
    }
    return current
  }, [treeData, selectedPath])

  const filteredData = useMemo(() => {
    if (!currentData) return null
    if (!activeLegend) return currentData
    const item = legendItems.find(l => l.label === activeLegend)
    if (!item) return currentData
    return filterTreeByRange(currentData, item.min, item.max)
  }, [currentData, activeLegend])

  const handleBreadcrumbClick = (index: number) => {
    if (index === -1) {
      setSelectedPath([])
      return
    }
    setSelectedPath((prev) => prev.slice(0, index + 1))
  }

  const handlePNChange = (value: string) => {
    setSelectedPN(value)
    setSelectedPath([])
  }

  const handleJourneyChange = (value: string[]) => {
    setSelectedJourneys(value)
    setSelectedPath([])
  }

  if (loading) {
    return (
      <div className={`ai-explore-shell ${theme === 'dark' ? 'is-dark' : 'is-light'}`}>
        <div className="ai-explore-loading">
          <Spin size="large" />
          <span style={{ marginTop: 16, opacity: 0.6 }}>正在加载 VOC 数据…</span>
        </div>
      </div>
    )
  }

  if (!filteredData) {
    return (
      <div className={`ai-explore-shell ${theme === 'dark' ? 'is-dark' : 'is-light'}`}>
        <div className="ai-explore-loading">
          <span style={{ opacity: 0.6 }}>暂无数据</span>
        </div>
      </div>
    )
  }

  return (
    <div ref={shellRef} className={`ai-explore-shell ${theme === 'dark' ? 'is-dark' : 'is-light'}`}>
      <header className="ai-explore-topbar">
        <div className="ai-explore-topbar-left">
          <h2 className="ai-explore-title">
            <span className="ai-explore-title-marker" />
            最佳体验云图
          </h2>

          <div className="ai-explore-topbar-divider" />

          <div className="ai-explore-pn-filter">
            <FilterOutlined style={{ opacity: 0.6 }} />
            <Select
              size="small"
              value={selectedPN}
              onChange={handlePNChange}
              options={pnOptions}
              style={{ minWidth: 140 }}
              popupMatchSelectWidth={false}
              getPopupContainer={() => (isFullscreen && shellRef.current) || document.body}
            />
          </div>

          <div className="ai-explore-topbar-divider" />

          <div className="ai-explore-pn-filter">
            <FilterOutlined style={{ opacity: 0.6 }} />
            <Select
              mode="multiple"
              size="small"
              value={selectedJourneys}
              onChange={handleJourneyChange}
              options={journeyOptions}
              placeholder="全部旅程"
              allowClear
              maxTagCount="responsive"
              style={{ minWidth: 140 }}
              popupMatchSelectWidth={false}
              getPopupContainer={() => (isFullscreen && shellRef.current) || document.body}
            />
          </div>

          <div className="ai-explore-topbar-divider" />

          <nav className="ai-explore-breadcrumbs" aria-label="Drill path">
            <button
              type="button"
              className="ai-breadcrumb-btn"
              onClick={() => handleBreadcrumbClick(-1)}
            >
              <HomeOutlined />
              <span>{treeData?.name}</span>
            </button>

            {selectedPath.map((name, index) => (
              <Fragment key={`${name}-${index}`}>
                <RightOutlined className="ai-breadcrumb-sep" />
                <button
                  type="button"
                  className={`ai-breadcrumb-btn ${index === selectedPath.length - 1 ? 'active' : ''}`}
                  onClick={() => handleBreadcrumbClick(index)}
                >
                  {name}
                </button>
              </Fragment>
            ))}
          </nav>
        </div>

        <div className="ai-explore-topbar-right">
          <div className="ai-explore-legend-wrap">
            <span className="ai-explore-legend-title">满意度:</span>
            <div className="ai-explore-legend-list">
              {legendItems.map((item) => {
                const isActive = activeLegend === item.label
                return (
                  <span
                    key={item.label}
                    className={`ai-explore-legend-item ${isActive ? 'is-active' : ''} ${activeLegend && !isActive ? 'is-dimmed' : ''}`}
                    style={{ backgroundColor: item.color, color: item.textColor }}
                    onClick={() => setActiveLegend(isActive ? null : item.label)}
                  >
                    {item.label}
                  </span>
                )
              })}
            </div>
          </div>

          <button
            type="button"
            className="ai-control-btn"
            onClick={toggleFullscreen}
            title={isFullscreen ? '退出全屏' : '全屏查看'}
          >
            {isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
          </button>
        </div>
      </header>

      <main className="ai-explore-main">
        <AITreemap
          data={filteredData}
          theme={theme}
          portalContainer={isFullscreen ? shellRef.current : null}
          onNodeClick={(path) => {
            let targetNode = filteredData
            for (const p of path) {
              targetNode = targetNode.children?.find((node) => node.name === p) || targetNode
            }

            if (targetNode.children && targetNode.children.length > 0) {
              setSelectedPath((prev) => [...prev, ...path])
            }
          }}
        />
      </main>
    </div>
  )
}

export default AIExplore
